import {apiClient, deviceApiClient} from './apiClient'; // Импорт на конфигурацията
import { jwtDecode } from 'jwt-decode'; // Добавяне на jwtDecode
export * from './api/access';
export * from './api/contacts';
export * from './api/deviceConfigs';
export * from './api/content';
export * from './api/campaignTimeline';
export * from './api/feedbackBoard';
export * from './api/fieldInstructions';
export * from './api/pmiFilterOptions';
export * from './api/pmiReports';
export * from './api/pmiSettings';
export * from './api/products';

const PMI_LOCATIONS_PAGE_LIMIT = 500;
let allPmiLocationsCache = null;
let allPmiLocationsCachePromise = null;

const buildQueryString = (filters = {}) => new URLSearchParams(
  Object.entries(filters || {}).filter(([, value]) => (
    value !== undefined
    && value !== null
    && value !== ''
  ))
).toString();


// Функция за вземане на устройства от основния API (on-labs)
export const getDevices = async (requestConfig = {}) => {
  const response = await apiClient.get('/devices', requestConfig);
  return response.data;
};

// Функция за вземане на неактивни устройства от локалния API (localhost:3004)
export const getInactiveDevices = async (requestConfig = {}) => {
  const response = await deviceApiClient.get('/devices/inactive', requestConfig);
  return response.data;
};

export const getHourlyDeviceStats = async (requestConfig = {}) => {
  const response = await deviceApiClient.get("/hourly", requestConfig);
  return response.data;
};

export const getUserProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/user/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const payload = response?.data || {};
    if (payload && typeof payload === 'object' && payload.user && typeof payload.user === 'object') {
      return {
        ...payload.user,
        menuAccess: payload.menuAccess || payload.user.menuAccess || [],
      };
    }
    return payload;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const getAuthProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/auth/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const payload = response?.data || {};
    const user = payload?.user && typeof payload.user === 'object' ? payload.user : {};

    return {
      ...user,
      roles: Array.isArray(user.roles) ? user.roles : [],
      permissions: Array.isArray(user.permissions) ? user.permissions : [],
      menuAccess: Array.isArray(payload.menuAccess)
        ? payload.menuAccess
        : Array.isArray(user.menuAccess)
        ? user.menuAccess
        : [],
    };
  } catch (error) {
    throw error;
  }
};

// Password reset (public, logged-out flow)
export const forgotPassword = async (email) => {
  const response = await apiClient.post(
    '/auth/forgot-password',
    { email },
    { skipAuthLogout: true }
  );
  return response.data;
};

export const validateResetToken = async (token) => {
  const response = await apiClient.get(
    `/auth/reset-password/${encodeURIComponent(token)}`,
    { skipAuthLogout: true }
  );
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await apiClient.post(
    '/auth/reset-password',
    { token, newPassword },
    { skipAuthLogout: true }
  );
  return response.data;
};

export const getCampaignDetailsAPI = async (campaignId) => {
  try {
    const response = await apiClient.get(`/campaigns/${campaignId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign details:', error);
    throw error;
  }
};


export const getAllAssignmentsAPI = async () => {
  try {
    const response = await apiClient.get('/campAssignments/all');
    return response.data;
  } catch (error) {
    console.error('Error fetching all assignments:', error);
    throw error;
  }
};

// Кампании към устройства API
export const assignCampaignToDeviceAPI = async (
  deviceId,
  campaignId,
  {
    isExclusive = false,
    activationStartAt = null,
    activationEndAt = null,
    source = '',
  } = {}
) => {
  try {
    const token = localStorage.getItem('token');
    const userEmail = jwtDecode(token)?.email; // Извличане на email от токена
    const response = await apiClient.post('/campAssignments/assign', {
      deviceId,
      campaignId,
      createdBy: userEmail, // Добавяне на създателя
      isActive: true, // По подразбиране активно
      isExclusive,
      activationStartAt,
      activationEndAt,
      source,
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning campaign to device:', error);
    throw error;
  }
};

export const unassignCampaignFromDeviceAPI = async (deviceId, campaignId) => {
  try {
    const token = localStorage.getItem('token');
    const userEmail = jwtDecode(token)?.email; // Извличане на email от токена

    const response = await apiClient.post('/campAssignments/unassign', {
      deviceId,
      campaignId,
      updatedBy: userEmail, // Добавяне на редактора
    });
    return response.data;
  } catch (error) {
    console.error('Error unassigning campaign from device:', error);
    throw error;
  }
};

export const unassignDeviceFromCampaignAPI = async (deviceId, campaignId) => {
  try {
    const token = localStorage.getItem('token');
    const userEmail = jwtDecode(token)?.email; // Извличане на email от токена
    const response = await apiClient.post('/campAssignments/unassign', {
      deviceId,
      campaignId,
      updatedBy: userEmail, // Може да се замени с реалния потребител
    });
    return response.data;
  } catch (error) {
    console.error('Error unassigning device from campaign:', error);
    throw error;
  }
};

export const getCampaignsForDeviceAPI = async (deviceId, { includeInactive = false } = {}) => {
  try {
    const response = await apiClient.get(`/campAssignments/device/${deviceId}`, {
      params: { includeInactive },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching campaigns for device:', error);
    throw error;
  }
};

export const getDevicesForCampaignAPI = async (campaignId, { includeInactive = false } = {}) => {
  try {
    const response = await apiClient.get(`/campAssignments/campaign/${campaignId}`, {
      params: { includeInactive },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching devices for campaign:', error);
    throw error;
  }
};

export const getHistoryForDeviceAPI = async (deviceId) => {
  try {
    const response = await apiClient.get(`/campAssignments/history?deviceId=${deviceId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching history for device:', error);
    throw error;
  }
};

export const getActionLogsAPI = async (filters) => {
  try {
    const queryParams = buildQueryString(filters);
    const response = await apiClient.get(`/campAssignments/logs?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching action logs:', error);
    throw error;
  }
};

export const fetchSimulationResults = async (page = 1, limit = 100, filters = {}) => {
  try {
    const response = await apiClient.post(`/simulation`, { page, limit, filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching simulation results:', error);
    throw error;
  }
};

//Devices
export const getActiveDevices = async () => {
  const response = await apiClient.get('/devices');
  return response.data;
};

export const getDashboardSummary = async () => {
  const response = await apiClient.get('/devices/dashboard-summary');
  return response.data;
};

export const getDashboardMapData = async () => {
  const response = await apiClient.get('/devices/map-data');
  return response.data;
};

export const getDevicesCount = async (filters) => {
  try {
    const response = await apiClient.post('/pmiDevices/devices/count', filters);
    return response.data.count;
  } catch (error) {
    console.error('Error fetching devices count:', error);
    throw error;
  }
};

export const getPmiDevices = async () => {
  try {
    const response = await apiClient.get('/pmiDevices');
    return response.data; // Адаптирайте отговора според бекенда
  } catch (error) {
    console.error('Error fetching PMI devices:', error);
    throw error;
  }
};

export const searchPmiDevices = async ({
  page = 1,
  limit = 100,
  sortBy = 'lastSeen',
  sortOrder = 'desc',
  filters = {},
  myDevices = false,
  userEmail = '',
  includeTotal = true,
  includeStats = false,
  bypassCache = false,
} = {}) => {
  try {
    // Only forward registrationStatus when it is explicitly set (e.g. 'unregistered').
    // The default ("Всички") must behave exactly like before, so drop the empty key.
    const outboundFilters = filters && typeof filters === 'object' ? { ...filters } : {};
    if (!outboundFilters.registrationStatus) {
      delete outboundFilters.registrationStatus;
    }
    const response = await apiClient.post('/pmiDevices/search', {
      page,
      limit,
      sortBy,
      sortOrder,
      filters: outboundFilters,
      myDevices,
      userEmail,
      includeTotal,
      includeStats,
      bypassCache,
    });
    return response.data;
  } catch (error) {
    console.error('Error searching PMI devices:', error);
    throw error;
  }
};

export const createPmiDevicesEventsSource = () => {
  const baseUrl = String(apiClient?.defaults?.baseURL || '').replace(/\/+$/, '');
  if (!baseUrl) {
    throw new Error('PMI API base URL is not configured');
  }
  return new EventSource(`${baseUrl}/pmiDevices/events`);
};

export const createServerHealthEventsSource = () => {
  const baseUrl = String(apiClient?.defaults?.baseURL || '').replace(/\/+$/, '');
  const token = localStorage.getItem('token');
  if (!baseUrl) {
    throw new Error('Server Health API base URL is not configured');
  }
  if (!token) {
    throw new Error('Server Health SSE token is not available');
  }
  return new EventSource(`${baseUrl}/server-health/events?token=${encodeURIComponent(token)}`);
};

export const getPmiProofOfPlayBootstrap = async ({
  timezone = 'Europe/Sofia',
  includeContentHistory = false,
  includeHistoryPeriods = false,
} = {}) => {
  const response = await apiClient.get('/pmiDevices/proof-of-play/bootstrap', {
    params: {
      timezone,
      includeContentHistory,
      includeHistoryPeriods,
    },
  });
  return response.data;
};

export const getPmiProofOfPlayContentHistory = async () => {
  const response = await apiClient.get('/pmiDevices/proof-of-play/content-history');
  return response.data;
};

export const getPmiProofOfPlayContentDevices = async ({ contentKey } = {}) => {
  const response = await apiClient.get('/pmiDevices/proof-of-play/content-devices', {
    params: { contentKey },
  });
  return response.data;
};

export const getPmiProofOfPlayBroadcastCertificate = async ({
  groupBy = 'all',
  from = '',
  to = '',
  limit = 100,
} = {}) => {
  const response = await apiClient.get('/pmiDevices/proof-of-play/broadcast-certificate', {
    params: {
      groupBy,
      from,
      to,
      limit,
    },
  });
  return response.data;
};

export const getPmiProofOfPlayHistoryPeriods = async ({
  timezone = 'Europe/Sofia',
} = {}) => {
  const response = await apiClient.get('/pmiDevices/proof-of-play/history-periods', {
    params: { timezone },
  });
  return response.data;
};

export const importPmiLocations = async ({
  file,
  filePath,
  sheetName = 'Account list',
  dryRun = true,
} = {}) => {
  let response;
  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheetName', sheetName);
    formData.append('dryRun', String(Boolean(dryRun)));
    if (filePath) {
      formData.append('filePath', filePath);
    }
    response = await apiClient.post('/pmiLocations/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } else {
    response = await apiClient.post('/pmiLocations/import', {
      filePath,
      sheetName,
      dryRun,
    });
  }
  if (!dryRun) {
    allPmiLocationsCache = null;
    allPmiLocationsCachePromise = null;
  }
  return response.data;
};

export const listPmiLocations = async (params = {}) => {
  const response = await apiClient.get('/pmiLocations', { params });
  return response.data;
};

// Exact KPI counts for locations-v2 (86caz11u3): { total, mapped, unmapped, withDevices,
// withoutDevices, active } for the given base filters (geo/deviceLinkage ignored server-side).
export const getPmiLocationStats = async (params = {}) => {
  const response = await apiClient.get('/pmiLocations/stats', { params });
  return response.data;
};

export const createPmiLocation = async (payload = {}) => {
  const response = await apiClient.post('/pmiLocations', payload);
  allPmiLocationsCache = null;
  allPmiLocationsCachePromise = null;
  return response.data;
};

export const updatePmiLocation = async (id, payload = {}) => {
  const response = await apiClient.put(`/pmiLocations/${id}`, payload);
  allPmiLocationsCache = null;
  allPmiLocationsCachePromise = null;
  return response.data;
};

export const getAllPmiLocationsCached = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && Array.isArray(allPmiLocationsCache)) {
    return allPmiLocationsCache;
  }

  if (!forceRefresh && allPmiLocationsCachePromise) {
    return allPmiLocationsCachePromise;
  }

  allPmiLocationsCachePromise = (async () => {
    const response = await listPmiLocations({
      all: true,
      limit: PMI_LOCATIONS_PAGE_LIMIT,
      sortBy: 'reevCode',
      sortOrder: 'asc',
    });
    const items = Array.isArray(response?.items) ? response.items : [];
    allPmiLocationsCache = items;
    allPmiLocationsCachePromise = null;
    return items;
  })().catch((error) => {
    allPmiLocationsCachePromise = null;
    throw error;
  });

  return allPmiLocationsCachePromise;
};

export const getPmiLocationFilters = async () => {
  const response = await apiClient.get('/pmiLocations/meta/filters');
  return response.data;
};

export const getPmiLocationByCode = async (reevCode) => {
  const response = await apiClient.get(`/pmiLocations/code/${encodeURIComponent(reevCode)}`);
  return response.data;
};

export const getPmiLocationById = async (id) => {
  const response = await apiClient.get(`/pmiLocations/${id}`);
  return response.data;
};

export const getPmiLocationDevices = async (id) => {
  const response = await apiClient.get(`/pmiLocations/${id}/devices`);
  return response.data;
};

export const getCustomFieldDefinitions = async (entityType) => {
  const response = await apiClient.get('/custom-fields', {
    params: { entityType },
  });
  return response.data;
};

export const createCustomFieldDefinition = async (payload = {}) => {
  const response = await apiClient.post('/custom-fields', payload);
  return response.data;
};

export const getCustomFieldValueSuggestions = async (entityType, fieldKey, q = '') => {
  const response = await apiClient.get('/custom-fields/value-suggestions', {
    params: { entityType, fieldKey, q },
  });
  return response.data;
};

export const getPublicPmiRegistration = async (params = {}, requestConfig = {}) => {
  const response = await apiClient.get('/pmiDevices/public/registration', {
    ...requestConfig,
    params,
  });
  return response.data;
};

export const reportPublicPmiRegistrationContext = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/context', payload);
  return response.data;
};

export const submitPublicPmiRegistrationFeedback = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/feedback', payload);
  return response.data;
};

export const unlockPublicPmiRegistrationEditing = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/unlock', payload);
  return response.data;
};

export const savePublicPmiRegistration = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration', payload);
  return response.data;
};

export const searchPublicPmiRegistrationLocations = async (params = {}) => {
  const response = await apiClient.get('/pmiDevices/public/registration/locations', { params });
  return response.data;
};

export const getPublicPmiRegistrationLocationMeta = async () => {
  const response = await apiClient.get('/pmiDevices/public/registration/locations/meta');
  return response.data;
};

export const createPublicPmiRegistrationLocation = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/location', payload);
  return response.data;
};

export const updatePublicPmiRegistrationLocation = async (payload = {}) => {
  const response = await apiClient.patch('/pmiDevices/public/registration/location', payload);
  return response.data;
};

export const requestPublicPmiRegistrationRestart = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/control/restart', payload);
  return response.data;
};

export const requestPublicPmiRegistrationRestartVlc = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/control/restart-vlc', payload);
  return response.data;
};

export const requestPublicPmiRegistrationUpdatePlaylist = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/control/update-playlist', payload);
  return response.data;
};

export const requestPublicPmiRegistrationShowDebugScreen = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/control/show-debug-screen', payload);
  return response.data;
};

// showScreen (RBPI >= 1.0.73): render an on-TV info screen (current/connecting/no-location) for
// holdSec, then auto-return. payload = { SN, MAC, code, editPassword, screen, holdSec, clientContext }.
export const requestPublicPmiRegistrationShowScreen = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/control/show-screen', payload);
  return response.data;
};

export const requestPublicPmiRegistrationUpdateFw = async (payload = {}) => {
  const response = await apiClient.post('/pmiDevices/public/registration/control/update-fw', payload);
  return response.data;
};

export const importTaTalAddresses = async ({
  file,
  filePath,
  sheetName = 'TA Addresses (2026)',
  dryRun = true,
} = {}) => {
  let response;
  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheetName', sheetName);
    formData.append('dryRun', String(Boolean(dryRun)));
    if (filePath) {
      formData.append('filePath', filePath);
    }
    response = await apiClient.post('/taTalAddresses/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } else {
    response = await apiClient.post('/taTalAddresses/import', {
      filePath,
      sheetName,
      dryRun,
    });
  }
  return response.data;
};

export const listTaTalAddresses = async (params = {}) => {
  const response = await apiClient.get('/taTalAddresses', { params });
  return response.data;
};

export const getTaTalAddressFilters = async () => {
  const response = await apiClient.get('/taTalAddresses/meta/filters');
  return response.data;
};

export const getTaTalAddressById = async (id) => {
  const response = await apiClient.get(`/taTalAddresses/${id}`);
  return response.data;
};

export const getUserDevices = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch('/api/collections/dynamic/collection_sheet1_1736411748084', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch user devices');
  }
  return response.json();
};

export const createDevice = async (deviceData) => {
  try {
    const response = await apiClient.post('/pmiDevices', deviceData);
    return response.data;
  } catch (error) {
    console.error('Error creating device:', error);
    throw error;
  }
};

export const fetchUniqueValues = async (field) => {
  const response = await apiClient.get(`/pmiDevices/unique-values/${field}`);
  return response.data;
};

export const fetchPmiDeviceFilterMeta = async (fields = []) => {
  const params = Array.isArray(fields) && fields.length > 0 ? { fields } : {};
  const response = await apiClient.get('/pmiDevices/meta/filters', { params });
  return response.data;
};

export const fetchUsers = async () => {
  const response = await apiClient.get('/contacts/users');
  return response.data;
};

export const updateDevice = async (id, deviceData) => {
  try {
    const normalizedId = String(id || '').trim();
    if (!normalizedId) {
      throw new Error('Device id is required.');
    }
    const token = localStorage.getItem('token');
    const response = await apiClient.put(`/pmiDevices/${normalizedId}`, deviceData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.device;
  } catch (err) {
    console.error('Error updating device:', err);
    throw err;
  }
};

// Device notes log (86cb0u4ax) — each note is a separate record with author + time.
export const getDeviceNotes = async (deviceId) => {
  const response = await apiClient.get(`/pmiDevices/${String(deviceId || '').trim()}/notes`);
  return response.data; // { notes: [...], legacyNote }
};

export const addDeviceNote = async (deviceId, text) => {
  const response = await apiClient.post(`/pmiDevices/${String(deviceId || '').trim()}/notes`, { text });
  return response.data.note;
};

export const deleteDeviceNote = async (deviceId, noteId) => {
  const response = await apiClient.delete(`/pmiDevices/${String(deviceId || '').trim()}/notes/${String(noteId || '').trim()}`);
  return response.data;
};

export const getPmiDeviceById = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/pmiDevices/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device by id:', error);
    throw error;
  }
};

export const getPmiDeviceEditLogs = async (id, params = { limit: 50 }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/pmiDevices/${id}/edit-logs`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device edit logs:', error);
    throw error;
  }
};

export const getPmiDeviceRegistrationContext = async (serialNumber) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/registration-context', {
      params: { serialNumber },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device registration context:', error);
    throw error;
  }
};

export const listPmiDeviceDuplicateSerialGroups = async (params = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/duplicates/serials', {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching duplicate serial groups:', error);
    throw error;
  }
};

export const resolvePmiDeviceDuplicateSerialGroup = async (payload = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post('/pmiDevices/duplicates/serials/resolve', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error resolving duplicate serial group:', error);
    throw error;
  }
};

export const getAllPmiDeviceEditLogs = async (params = { page: 1, limit: 50 }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/edit-logs/all', {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all PMI device edit logs:', error);
    throw error;
  }
};

export const revertPmiDeviceEditLog = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/edit-logs/${id}/revert`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error reverting PMI device edit log:', error);
    throw error;
  }
};

export const requestPmiDeviceRestart = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/restart`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device restart:', error);
    throw error;
  }
};

// showScreen (RBPI >= 1.0.73): render an on-TV info screen for holdSec, then auto-return. Non-
// destructive. screen ∈ current | connecting | no-location. Waits server-side for the device ack.
// Per-user UI preferences (e.g. pmi-devices-v2 visible columns). Server-backed, keyed by user.
export const getUserUiPrefs = async () => {
  const token = localStorage.getItem('token');
  const response = await apiClient.get('/user/ui-prefs', { headers: { Authorization: `Bearer ${token}` } });
  return response.data;
};
export const updateUserUiPrefs = async (patch = {}) => {
  const token = localStorage.getItem('token');
  const response = await apiClient.put('/user/ui-prefs', patch, { headers: { Authorization: `Bearer ${token}` } });
  return response.data;
};

export const requestPmiDeviceShowScreen = async (id, { screen = 'current', holdSec = 30 } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/show-screen`, { screen, holdSec }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device showScreen:', error);
    throw error;
  }
};

export const requestPmiDeviceFwUpdate = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/update-fw`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device firmware update:', error);
    throw error;
  }
};

export const requestPmiDeviceWifiHotspot = async (id, { ssid, password, ttlMinutes } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/wifi-hotspot`, { ssid, password, ttlMinutes }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device WiFi hotspot:', error);
    throw error;
  }
};

export const requestPmiDeviceWifiHotspotStop = async (id, { prevSsid } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/wifi-hotspot-stop`, { prevSsid }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device WiFi hotspot stop:', error);
    throw error;
  }
};

// Screen control (HDMI-CEC). Turn the connected TV on/off, reclaim our input, or refresh CEC/display
// info on demand. RBPI-only in effect. Contract: ds-platform-docs/platform/116-RPI_CEC_DISPLAY_SCREEN_CONTROL.md.
export const requestPmiDeviceCecPower = async (id, { on = true } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/cec-power`, { on: !!on }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device cecPower:', error);
    throw error;
  }
};

export const requestPmiDeviceCecActiveSource = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/cec-active-source`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device cecActiveSource:', error);
    throw error;
  }
};

// kind: 'cec' | 'display' — asks the device for a fresh scan; the ack carries the block back.
export const requestPmiDeviceScreenInfo = async (id, { kind = 'cec' } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/screen-info`, { kind }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device screen info:', error);
    throw error;
  }
};

// Latest BLE snapshot (presence/peers/decoded sensors) for a device (86caznubx). Read-only.
export const getPmiDeviceBle = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/pmiDevices/${id}/ble`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device BLE:', error);
    return null;
  }
};

// Copy selected settings/segmentation fields from a source device onto a target (86cazzq46).
// Revertable via revertPmiDeviceEditLog(logId) using the returned logId.
export const copyPmiDeviceSettings = async (targetId, { sourceDeviceId, fields } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(
      `/pmiDevices/${targetId}/copy-settings`,
      { sourceDeviceId, fields },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error copying device settings:', error);
    throw error;
  }
};

// Credential-scrubbed config the device receives (vxtConfiguration) → device-preview „Настройки" tab.
export const getPmiDeviceConfig = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/pmiDevices/${id}/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device config:', error);
    return null;
  }
};

export const requestPmiDeviceBulkRestart = async ({ deviceIds = [] } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post('/pmiDevices/control/restart/bulk', { deviceIds }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device bulk restart:', error);
    throw error;
  }
};

export const getPmiDeviceActiveBulkRestartJob = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/control/restart/bulk/active', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error loading active PMI bulk restart job:', error);
    throw error;
  }
};

export const getPmiDeviceBulkRestartJob = async (jobId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/pmiDevices/control/restart/bulk/${jobId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error loading PMI bulk restart job:', error);
    throw error;
  }
};

export const requestPmiDevicePushTest = async (id, payload = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/push-test`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device push test:', error);
    throw error;
  }
};

export const requestPmiDeviceTunnelControl = async (id, { action } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/tunnel`, { action }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device tunnel control:', error);
    throw error;
  }
};

export const requestPmiDeviceReserveTunnelControl = async (id, { action } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/pmiDevices/${id}/control/tunnel-reserve`, { action }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting PMI device reserve tunnel control:', error);
    throw error;
  }
};

export const requestPmiDeviceTunnelAudit = async ({ deviceIds = [] } = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post('/pmiDevices/control/tunnel/audit', { deviceIds }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error starting PMI device tunnel audit:', error);
    throw error;
  }
};

export const getPmiDeviceTunnelAuditJob = async (jobId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/pmiDevices/control/tunnel/audit/${jobId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device tunnel audit job:', error);
    throw error;
  }
};

export const getPmiDeviceActiveTunnelAuditJob = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/control/tunnel/audit/active', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching active PMI device tunnel audit job:', error);
    throw error;
  }
};

export const getPmiDeviceTunnelAuditLiveSummary = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/tunnel-audit/live-summary', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device tunnel audit live summary:', error);
    throw error;
  }
};

export const getPmiDeviceStatusLogAggregates = async (params = {}) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/pmiDevices/status-logs/aggregated', {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PMI device status log aggregates:', error);
    throw error;
  }
};

export const getCampaignEditLogs = async (id, params = { limit: 50 }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/campaigns/${id}/edit-logs`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign edit logs:', error);
    throw error;
  }
};

export const getAllCampaignEditLogs = async (params = { page: 1, limit: 50 }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/campaigns/edit-logs/all', {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all campaign edit logs:', error);
    throw error;
  }
};

export const getFileEditLogs = async (id, params = { limit: 50 }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/media/${id}/edit-logs`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching file edit logs:', error);
    throw error;
  }
};

export const getAllFileEditLogs = async (params = { page: 1, limit: 50 }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/media/edit-logs/all', {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all file edit logs:', error);
    throw error;
  }
};

export const toggleDeviceHiddenStatus = async (deviceIds, hiddenStatus) => {
  try {
    const response = await apiClient.put('/pmiDevices/hide', {
      deviceIds,
      hidden: hiddenStatus,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating hidden status:', error);
    throw error;
  }
};

export const listCategorySchedules = async ({ segmentId = '', search = '' } = {}) => {
  const response = await apiClient.get('/categorySchedules', {
    params: { segmentId, search },
  });
  return response.data;
};

export const syncCategorySchedules = async (payload) => {
  const response = await apiClient.post('/categorySchedules/sync', payload);
  return response.data;
};

export const deleteCategorySchedule = async (scheduleId) => {
  const response = await apiClient.delete(`/categorySchedules/${scheduleId}`);
  return response.data;
};
